import { useState, useCallback, useEffect, useRef } from 'react';
import { HttpStatusCode } from 'axios';
import { useApiCall } from 'use-api-call';
import authSvc from '@/services/auth/authSvc';
import {
  UseEmailResendProps,
  UseEmailResendReturn,
  ValidationError,
} from '../types';
import {
  MAX_RESEND_ATTEMPTS,
  RESEND_COOLDOWN_MS,
  RESEND_EMAIL_REQUEST_MS,
  RESEND_EMAIL_SUCCESS_MS,
  RESET_ATTEMPTS_MS,
} from '../constants';

export const useEmailResend = ({
  userEmail,
}: UseEmailResendProps): UseEmailResendReturn => {
  const [resendSuccess, setResendSuccess] = useState<boolean>(false);
  const [resendError, setResendError] = useState<ValidationError | null>(null);
  const [lastResendTime, setLastResendTime] = useState<number>(0);
  const [resendAttempts, setResendAttempts] = useState<number>(0);
  const [timeUntilNextResend, setTimeUntilNextResend] = useState<number>(0);

  const cooldownIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const resetAttemptsTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const successTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const {
    data,
    invoke: resendVerificationEmailRequest,
    loading: isResending,
    error: apiError,
  } = useApiCall(() => authSvc.resendVerificationEmail(userEmail || ''));

  const canResend = useCallback(() => {
    const now = Date.now();
    const timeSinceLastResend = now - lastResendTime;
    const hasExceededAttempts = resendAttempts >= MAX_RESEND_ATTEMPTS;
    const isInCooldown = timeSinceLastResend < RESEND_COOLDOWN_MS;

    return !isResending && !hasExceededAttempts && !isInCooldown && !!userEmail;
  }, [isResending, resendAttempts, lastResendTime, userEmail]);

  const resendEmail = useCallback(async () => {
    if (!canResend()) {
      return;
    }

    setResendError(null);
    setResendSuccess(false);

    try {
      const now = Date.now();
      setLastResendTime(now);
      setResendAttempts((prev) => prev + 1);
      setResendSuccess(true);
      resendVerificationEmailRequest();
    } catch (error) {
      setResendError(error as ValidationError);
    }
  }, [canResend, resendVerificationEmailRequest]);

  const resetResendState = useCallback(() => {
    setResendSuccess(false);
    setResendError(null);
    setLastResendTime(0);
    setResendAttempts(0);
    setTimeUntilNextResend(0);

    if (cooldownIntervalRef.current) {
      clearInterval(cooldownIntervalRef.current);
      cooldownIntervalRef.current = null;
    }

    if (resetAttemptsTimeoutRef.current) {
      clearTimeout(resetAttemptsTimeoutRef.current);
      resetAttemptsTimeoutRef.current = null;
    }

    if (successTimeoutRef.current) {
      clearTimeout(successTimeoutRef.current);
      successTimeoutRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (lastResendTime > 0) {
      cooldownIntervalRef.current = setInterval(() => {
        const now = Date.now();
        const timeSinceLastResend = now - lastResendTime;
        const remaining = Math.max(0, RESEND_COOLDOWN_MS - timeSinceLastResend);

        setTimeUntilNextResend(Math.ceil(remaining / RESEND_EMAIL_REQUEST_MS));

        if (remaining <= 0 && cooldownIntervalRef.current) {
          clearInterval(cooldownIntervalRef.current);
          cooldownIntervalRef.current = null;
        }
      }, RESEND_EMAIL_REQUEST_MS);
    }

    return () => {
      if (cooldownIntervalRef.current) {
        clearInterval(cooldownIntervalRef.current);
      }
    };
  }, [lastResendTime]);

  useEffect(() => {
    if (resendAttempts > 0) {
      if (resetAttemptsTimeoutRef.current) {
        clearTimeout(resetAttemptsTimeoutRef.current);
      }

      resetAttemptsTimeoutRef.current = setTimeout(() => {
        setResendAttempts(0);
      }, RESET_ATTEMPTS_MS);
    }

    return () => {
      if (resetAttemptsTimeoutRef.current) {
        clearTimeout(resetAttemptsTimeoutRef.current);
      }
    };
  }, [resendAttempts]);

  useEffect(() => {
    if (data?.status === HttpStatusCode.Ok) {
      setResendSuccess(true);
      setResendError(null);

      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
      }

      successTimeoutRef.current = setTimeout(() => {
        setResendSuccess(false);
      }, RESEND_EMAIL_SUCCESS_MS);
    }
  }, [data]);

  useEffect(() => {
    if (apiError) {
      const error = apiError as ValidationError;
      setResendError(error);
      setResendSuccess(false);
    }
  }, [apiError]);

  useEffect(
    () => () => {
      if (cooldownIntervalRef.current) {
        clearInterval(cooldownIntervalRef.current);
      }
      if (resetAttemptsTimeoutRef.current) {
        clearTimeout(resetAttemptsTimeoutRef.current);
      }
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
      }
    },
    []
  );

  return {
    isResending,
    resendError,
    resendSuccess,
    canResend: canResend(),
    timeUntilNextResend,
    resendEmail,
    resetResendState,
  };
};

export default useEmailResend;
